import resolveFrontendModule from "@app/resolveModule/frontend";
import { useRouter } from "@core/Api/useRouter";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import EnterpriseApi from "@ext/enterprise/EnterpriseApi";
import t from "@ext/localization/locale/translate";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, LoadingButtonTemplate } from "@ui-kit/Button";
import { Form, FormBody, FormField, FormStack } from "@ui-kit/Form";
import { Input } from "@ui-kit/Input";
import { useCallback, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import FetchService from "../../../../ui-logic/ApiServices/FetchService";
import { getGesSignInUrl } from "./utils/getGesSignInUrl";

const formSchema = z.object({
	gesUrl: z.string({ message: t("must-be-not-empty") }),
});

export const GesCorporateSignIn = () => {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const router = useRouter();

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		mode: "onChange",
		defaultValues: {
			gesUrl: PageDataContextService.value.conf.activeGesUrl ?? "",
		},
	});

	const abortController = useRef(new AbortController());

	const [isValidChecking, setIsValidChecking] = useState(false);

	const checkIfGesUrlIsValid = useCallback(
		async (gesUrl: string) => {
			setIsValidChecking(true);

			let errorMessage: string;
			if (URL.canParse(gesUrl)) {
				abortController.current.abort();
				abortController.current = new AbortController();

				const normalizedGesUrl = new URL(gesUrl).origin;
				const checkResult = await new EnterpriseApi(normalizedGesUrl).check();
				if (checkResult) errorMessage = "";
				else errorMessage = t("forms.sign-in-ges-tauri.errors.enterpriseServerIsNotAvailable");
			} else errorMessage = t("forms.sign-in-ges-tauri.errors.urlIsNotValid");

			setIsValidChecking(false);

			if (errorMessage) form.setError("gesUrl", { message: errorMessage });
			else form.clearErrors("gesUrl");

			return !errorMessage;
		},
		[form.setError, form.clearErrors],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: it's ok
	const handleSignIn = useCallback(async () => {
		const rawGesUrl = form.getValues("gesUrl")?.trim();
		if (!rawGesUrl) return;

		const isGesUrlValid = await checkIfGesUrlIsValid(rawGesUrl);
		if (!isGesUrlValid) return;

		const gesUrl = new URL(rawGesUrl).origin;

		await FetchService.fetch(apiUrlCreator.setGesUrl(gesUrl));
		const authUrl = getGesSignInUrl(gesUrl, false);
		await resolveFrontendModule("enterpriseLogin")(authUrl, apiUrlCreator, router);
	}, [form.getValues, apiUrlCreator, router, checkIfGesUrlIsValid]);

	const gesUrl = form.watch("gesUrl");
	const isSubmitDisabled = !gesUrl.trim();

	return (
		<Form asChild {...form}>
			<form onSubmit={form.handleSubmit(handleSignIn)}>
				<FormBody>
					<FormStack>
						<FormField
							control={({ field }) => (
								<Input
									autoFocus
									placeholder={t("forms.sign-in-ges-tauri.props.gesUrl.placeholder")}
									{...field}
								/>
							)}
							description={t("forms.sign-in-ges-tauri.props.gesUrl.description")}
							labelClassName="w-full"
							layout="vertical"
							name="gesUrl"
							required
							title={t("forms.sign-in-ges-tauri.props.gesUrl.name")}
						/>
						<div className="flex justify-center w-full">
							{isValidChecking ? (
								<LoadingButtonTemplate
									className="w-full"
									text={t("enterprise-guest.buttons.corporateLoginButton")}
								/>
							) : (
								<Button
									className="w-full"
									disabled={isSubmitDisabled}
									startIcon="building-2"
									type="submit"
								>
									{t("enterprise-guest.buttons.corporateLoginButton")}
								</Button>
							)}
						</div>
					</FormStack>
				</FormBody>
			</form>
		</Form>
	);
};
